/**
 * O bloco de cabeçalho: quem é o cliente, qual é a OS, quando vence.
 *
 * Aqui mora o campo que o Jeferson marcou primeiro em verde — a previsão de
 * entrega. É por ela que a fila do produção é ordenada, então falhar em
 * lê-la manda a OS para a quarentena em vez de deixá-la entrar sem prazo.
 *
 * O carimbo "<< OS CONCLUÍDA >>" é impresso POR CIMA do título, sobreposto. Em
 * leitura por ordem de conteúdo ele corrompe a linha de emissão; pela posição,
 * é só mais um item, num x próprio.
 */

import type { CabecalhoLido } from '@pcp/dominio';
import { agruparEmLinhas, type Linha } from './grade.js';
import type { ItemDeTexto } from './pdf.js';

const RE_RODAPE = /Gráfica Boa União\.\s*Pág:\s*(\d+)\s*\/\s*(\d+)/;
const RE_OS = /^ORDEM DE SERVIÇO:\s*(\d+)/;
const RE_EMISSAO =
  /Emissão:\s*(\d{2}\/\d{2}\/\d{2,4})-(\d{1,2}:\d{2})-(\S+?)\((.+?)\)(?:\s*-\s*Alt\.:\s*(\d{2}\/\d{2}\/\d{2,4})-(\d{1,2}:\d{2})\((.+?)\))?/;
const RE_PREVISAO = /Previsão de entrega\s*:\s*(\d{2}\/\d{2}\/\d{2,4})-(\d{1,2}:\d{2})-(\S+)/;
const RE_CARIMBO = /^<<\s*(.+?)\s*>>$/;
const RE_PRODUTO = /^([\d.]+)\s+(.+)$/;
const RE_CLIENTE = /^:\s*(\d+)\s*-\s*(.*)$/;
const RE_ORCAMENTO = /^:\s*(\d+)\.(\d+)\s+por\s+(.+)$/;

const ROTULOS = new Set(['Cliente', 'Contato', 'Orçam.', 'Transport.', 'Observações gerais:']);

/** Onde o cabeçalho termina: na primeira caixa de plano, ou no y de segurança. */
export function fimDoCabecalho(itens: readonly ItemDeTexto[]): number {
  const plano = itens
    .filter((i) => /^Plano\s*#\d/.test(i.texto))
    .sort((a, b) => a.y - b.y)[0];
  return plano ? plano.y - 1 : 190;
}

export function lerCabecalho(
  itens: readonly ItemDeTexto[],
  avisos: string[],
): CabecalhoLido | null {
  const limite = fimDoCabecalho(itens);
  const doCabecalho = itens.filter((i) => i.y < limite);
  const linhas = agruparEmLinhas(doCabecalho);

  let clienteCurto = '';
  let pagina = 1;
  let paginas = 1;
  let osNumero = 0;
  let carimbo: string | null = null;
  let emissao: CabecalhoLido['emissao'] | null = null;
  let alteracao: CabecalhoLido['alteracao'] = null;
  let previsaoEntrega: CabecalhoLido['previsaoEntrega'] = null;
  let clienteCodigo: string | null = null;
  let clienteNome: string | null = null;
  let contato: CabecalhoLido['contato'] = null;
  let orcamento: CabecalhoLido['orcamento'] = null;
  let transportadora: string | null = null;
  const observacoesGerais: string[] = [];

  let yDaOs = 0;
  let yDoPrimeiroRotulo = Number.POSITIVE_INFINITY;
  let xDasObservacoes: number | null = null;

  for (const linha of linhas) {
    for (const item of linha.itens) {
      const t = item.texto;

      const rodape = RE_RODAPE.exec(t);
      if (rodape) {
        pagina = Number(rodape[1]);
        paginas = Number(rodape[2]);
        continue;
      }

      const carimboAchado = RE_CARIMBO.exec(t);
      if (carimboAchado) {
        carimbo = carimboAchado[1] ?? null;
        continue;
      }

      const os = RE_OS.exec(t);
      if (os) {
        osNumero = Number(os[1]);
        yDaOs = item.y;
        continue;
      }

      const em = RE_EMISSAO.exec(t);
      if (em) {
        emissao = {
          data: em[1] ?? '',
          hora: em[2] ?? '',
          diaSemana: em[3] ?? '',
          usuario: em[4] ?? '',
        };
        if (em[5]) {
          alteracao = { data: em[5], hora: em[6] ?? '', usuario: em[7] ?? '' };
        }
        continue;
      }

      const pv = RE_PREVISAO.exec(t);
      if (pv) {
        previsaoEntrega = { data: pv[1] ?? '', hora: pv[2] ?? '', diaSemana: pv[3] ?? '' };
        continue;
      }

      if (t === 'Observações gerais:') {
        xDasObservacoes = item.x;
        yDoPrimeiroRotulo = Math.min(yDoPrimeiroRotulo, item.y);
        continue;
      }

      if (ROTULOS.has(t) && item.x < 100) {
        yDoPrimeiroRotulo = Math.min(yDoPrimeiroRotulo, item.y);
        const valor = valorDoRotulo(linha, item.x);
        aplicarRotulo(t, valor);
        continue;
      }
    }
  }

  function aplicarRotulo(rotulo: string, valor: string | null): void {
    if (!valor) return;
    if (rotulo === 'Cliente') {
      const m = RE_CLIENTE.exec(valor);
      if (m) {
        clienteCodigo = m[1] ?? null;
        clienteNome = (m[2] ?? '').trim() || null;
      } else {
        clienteNome = valor.replace(/^:\s*/, '').trim() || null;
      }
    } else if (rotulo === 'Contato') {
      contato = lerContato(valor.replace(/^:\s*/, ''));
    } else if (rotulo === 'Orçam.') {
      const m = RE_ORCAMENTO.exec(valor);
      orcamento = m
        ? { numero: m[1] ?? '', item: m[2] ?? '', vendedor: (m[3] ?? '').trim() }
        : null;
      if (!m) avisos.push(`Orçamento em formato não reconhecido: "${valor}"`);
    } else if (rotulo === 'Transport.') {
      transportadora = valor.replace(/^:\s*/, '').trim() || null;
    }
  }

  // Cliente curto: o texto solto no alto, à esquerda do rodapé.
  const alto = doCabecalho
    .filter((i) => i.y < 45 && i.x < 300)
    .sort((a, b) => a.x - b.x);
  clienteCurto = alto.map((i) => i.texto).join(' ').trim();

  // Produto: o que está à esquerda, entre a linha da OS e o primeiro rótulo.
  const produtoItens = doCabecalho
    .filter((i) => i.x < 120 && i.y > yDaOs + 2 && i.y < yDoPrimeiroRotulo - 2)
    .sort((a, b) => a.y - b.y || a.x - b.x);
  const produtoTexto = produtoItens.map((i) => i.texto).join(' ').trim();
  const mp = RE_PRODUTO.exec(produtoTexto);
  const quantidade = mp ? Number((mp[1] ?? '0').replace(/\./g, '')) : 0;
  const produto = mp ? (mp[2] ?? '').trim() : produtoTexto;
  if (produtoTexto && !mp) {
    avisos.push(`Linha do produto sem quantidade na frente: "${produtoTexto}"`);
  }

  // Observações gerais: tudo que fica abaixo do rótulo, na mesma coluna.
  if (xDasObservacoes !== null) {
    const x = xDasObservacoes;
    const yRotulo = doCabecalho.find(
      (i) => i.texto === 'Observações gerais:' && Math.abs(i.x - x) < 1,
    )?.y;
    if (yRotulo !== undefined) {
      const linhasObs = agruparEmLinhas(
        doCabecalho.filter((i) => Math.abs(i.x - x) <= 8 && i.y > yRotulo + 2),
      );
      for (const l of linhasObs) {
        const texto = l.itens.map((i) => i.texto).join(' ').trim();
        if (texto) observacoesGerais.push(texto);
      }
    }
  }

  if (!osNumero || !emissao) return null;

  return {
    clienteCurto,
    pagina,
    paginas,
    osNumero,
    emissao,
    alteracao,
    previsaoEntrega,
    quantidade,
    produto,
    carimbo,
    clienteCodigo,
    clienteNome,
    contato,
    orcamento,
    transportadora,
    observacoesGerais,
  };
}

/** O valor de um rótulo é o que vem depois dele, na mesma linha, à esquerda. */
function valorDoRotulo(linha: Linha, xDoRotulo: number): string | null {
  const depois = linha.itens
    .filter((i) => i.x > xDoRotulo + 5 && i.x < 280)
    .sort((a, b) => a.x - b.x);
  const texto = depois.map((i) => i.texto).join(' ').trim();
  return texto || null;
}

/**
 * "Sarah - (24)2255-0660 - compras2@hcnsc.com.br", com partes que faltam.
 *
 * A gráfica escreve o contato em três pedaços separados por hífen, mas nenhum
 * dos três é garantido:
 *
 *   Sarah - (24)2255-0660 - compras2@hcnsc.com.br    os três
 *   - (24)2255-2323 - meioambiente@tresrios.rj.gov.br  sem nome
 *   Padre Jeferson - (24)99263-0753                   sem e-mail
 *
 * Dividir por hífen e confiar na posição daria errado duas vezes: o telefone
 * tem hífen dentro, e o nome vazio deixa a linha começando por hífen — foi
 * assim que o telefone da Secretaria de Meio Ambiente virou "nome" na primeira
 * versão. Então: hífen inicial significa nome ausente, e cada pedaço é
 * reconhecido pelo que ele é (arroba = e-mail, começa com dígito ou parêntese
 * = telefone).
 */
export function lerContato(texto: string): CabecalhoLido['contato'] {
  let resto = texto.trim();
  const partes: string[] = [];
  if (resto.startsWith('-')) {
    partes.push(''); // nome ausente
    resto = resto.replace(/^-\s*/, '');
  }
  partes.push(...resto.split(' - ').map((p) => p.trim()));

  let nome: string | null = null;
  let telefone: string | null = null;
  let email: string | null = null;

  for (const parte of partes) {
    if (!parte) continue;
    if (parte.includes('@')) email = parte;
    else if (/^\(?\d/.test(parte)) telefone = parte;
    else if (nome === null) nome = parte;
    else nome = `${nome} - ${parte}`;
  }

  if (nome === null && telefone === null && email === null) return null;
  return { nome, telefone, email };
}
